import { useIntl } from 'react-intl';

import { useTrackEventAmplitude } from '../../../core/Tracking/Amplitude/Amplitude';
import { Button } from '../../../design-system/components/form/button/Button';
import { Typography } from '../../../design-system/components/typography/Typography';
import { Carousel, useCarousel } from '../../../domains/Layout/Carousel/Carousel';
import {
    DestinationArticleCard,
    DestinationArticleCardProps,
} from '../../../domains/TravelGuide/Article/DestinationArticleCard';
import { Destination } from '../../../resources/destination/domain/types/destination';
import { useUrlGenerator } from '../../hooks/useUrlGenerator';
import { IntrinsicElement } from '../../utilities/types/IntrinsicElement';
import { PickDomain } from '../../utilities/types/PickDomain';

import style from './DestinationArticlesCarouselSection.module.css';

export function DestinationArticlesCarouselSection({
    destination,
    articles,
    title: titleOverride,
}: {
    destination: PickDomain<Destination, 'id' | 'name' | 'preposition' | 'slug'>;
    articles: Array<DestinationArticleCardProps['article']>;
    title?: Partial<{
        as: IntrinsicElement;
        children: string;
    }>;
}) {
    const { formatMessage } = useIntl();
    const { generatePath } = useUrlGenerator();
    const { trackEvent } = useTrackEventAmplitude();
    const carousel = useCarousel();

    const TRACKING_BLOCK = 'articles' as const;

    const title = {
        as: 'div' as const,
        children: formatMessage({ id: 'global.articleSection.title' }),
        ...titleOverride,
        scale: 'primary-l-extra-bold' as const,
        className: style.title,
    };

    return (
        <>
            <Typography {...title} />

            <Carousel
                {...carousel}
                className={style.carousel}
                slidesSize="large"
                slidesVisibleRules={{
                    sm: 2,
                    md: 2,
                    lg: 3,
                    xl: 3,
                    xxl: 4,
                }}
                trackingInformation={{
                    navClick: {
                        block: TRACKING_BLOCK,
                    },
                }}
            >
                {articles.map((article, index) => (
                    <DestinationArticleCard
                        key={article.documentId}
                        article={article}
                        image={{
                            sizes: '300px',
                        }}
                        trackingInformation={{
                            click: {
                                block: TRACKING_BLOCK,
                                card_number: index + 1,
                            },
                        }}
                    />
                ))}
            </Carousel>

            <div className={style.buttonContainer}>
                <Button
                    priority="secondary"
                    href={generatePath('destination_article_list', {
                        destinationSlug: destination.slug,
                    })}
                    onClick={() => {
                        trackEvent((ampli, defaultProperties) => {
                            ampli.destinationArticleListClicked({
                                ...defaultProperties,
                                block: TRACKING_BLOCK,
                                destination_id: destination.id,
                                destination_slug: destination.slug,
                                article_type:
                                    articles[0].type === 'general_inspiration'
                                        ? 'general_inspiration'
                                        : 'general_practical',
                            });
                        });
                    }}
                >
                    {formatMessage({ id: 'global.cta.seeAllArticles' })}
                </Button>
            </div>
        </>
    );
}
